import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Gauge,
  Megaphone,
  NotebookPen,
  Palette,
  Search,
  ShieldCheck,
} from 'lucide-react';

import PixelBackground from '@/components/PixelBackground';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import Seo from '@/components/Seo';
import { JsonLd } from '@/components/JsonLd';
import Display from '@/components/typography/Display';
import Button from '@/components/ui/Button';
import { Container, HeroSection, Section } from '@/components/ui/Section';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocalizedPath, stripLanguagePrefix, withLanguagePrefix } from '@/lib/locale';
import { BASE_URL, getHreflangLinks } from '@/seo/meta';
import {
  generateBreadcrumbSchema,
  generateFAQSchema,
  generateOrganizationSchema,
  generateServiceSchema,
  generateWebsiteSchema,
} from '@/lib/seo';
import { trackCTA, trackServiceView } from '@/lib/analytics';
import { trackScroll } from '@/lib/scroll';
import { getServiceMeta } from '@/seo/meta';

const serviceName = 'Κατασκευή Ιστοσελίδων';
const serviceSlug = 'web-design';

type Locale = 'en' | 'el';

type Metric = {
  value: string;
  label: string;
};

type HeroCardContent = {
  eyebrow: string;
  title: string;
  description: string;
  bullets: string[];
  contactLabel: string;
};

type HeroContent = {
  eyebrow: string;
  leading: string;
  accent?: string;
  accentLines?: string[];
  description: string;
  highlights: string[];
  metrics: Metric[];
  primaryCta: string;
  secondaryCta: string;
  card: HeroCardContent;
};

type PillarKey = 'strategy' | 'experience' | 'engineering' | 'enablement';

type PillarCopy = {
  title: string;
  description: string;
  bullets: string[];
};

type ProcessStep = {
  title: string;
  description: string;
  deliverable?: string;
  duration?: string;
  bullets?: string[];
};

type LaunchGroup = {
  title: string;
  bullets: string[];
};

type FaqIntroContent = {
  eyebrow: string;
  heading: string;
  description: string;
};

type ClosingContent = {
  eyebrow: string;
  title: string;
  description: string;
  highlights: string[];
  primaryCta: string;
  secondaryCta: string;
};

const heroCopy: Record<Locale, HeroContent> = {
  en: {
    eyebrow: 'Website Development',
    leading: 'Websites that bring clients and work flawlessly.',
    description:
      'We design and build websites that present your business clearly, run reliably, and scale with your growth.',
    highlights: [],
    metrics: [
      { value: 'From 2–6 weeks', label: 'Average delivery time' },
      { value: 'Ready to use', label: 'Fully functional at launch' },
      { value: 'Long-term partnerships', label: 'Most clients return' },
    ],
    primaryCta: 'Book a free consultation',
    secondaryCta: 'See our work',
    card: {
      eyebrow: 'Within 30 days',
      title: 'What launches with every engagement',
      description:
        'We combine strategy, design systems, and engineering into one operating rhythm so the first release ships production-ready.',
      bullets: [
        'Audience insight & UX architecture blueprint',
        'Responsive design system in Figma and code',
        'Headless CMS configured with workflows & training',
      ],
      contactLabel: 'Prefer a direct line?',
    },
  },
  el: {
    eyebrow: 'Κατασκευή Ιστοσελίδων',
    leading: 'Ιστοσελίδες που φέρνουν πελάτες και δουλεύουν σωστά.',
    description:
      'Σχεδιάζουμε και κατασκευάζουμε ιστοσελίδες που παρουσιάζουν σωστά την επιχείρησή σας, λειτουργούν χωρίς προβλήματα και μπορούν να εξελιχθούν μαζί σας.',
    highlights: [],
    metrics: [
      { value: 'Από 2–6 εβδομάδες', label: 'Μέσος χρόνος παράδοσης' },
      { value: 'Έτοιμη για χρήση', label: 'Πλήρως λειτουργική στο λανσάρισμα' },
      { value: 'Συνεργασίες με συνέχεια', label: 'Οι περισσότεροι πελάτες επιστρέφουν' },
    ],
    primaryCta: 'Κλείστε δωρεάν συμβουλευτική',
    secondaryCta: 'Δείτε έργα μας',
    card: {
      eyebrow: 'Μέσα στον πρώτο μήνα',
      title: 'Τι παραδίδουμε σε κάθε συνεργασία',
      description:
        'Συνδυάζουμε στρατηγική, design system και ανάπτυξη σε ένα ενιαίο operating rhythm ώστε το πρώτο release να είναι production-ready.',
      bullets: [
        'Blueprint με insights κοινού & UX αρχιτεκτονική',
        'Responsive design system σε Figma και κώδικα',
        'Ρυθμισμένο headless CMS με workflows & training',
      ],
      contactLabel: 'Θέλετε άμεση επαφή;',
    },
  },
};

const approachCopy: Record<
  Locale,
  {
    eyebrow: string;
    heading: string;
    description: string;
  }
> = {
  en: {
    eyebrow: 'What the website build includes',
    heading: 'A professional website is more than appearance.',
    description:
      'It is structure, content, and proper functionality so visitors immediately understand what you do and how to contact you.',
  },
  el: {
    eyebrow: 'Τι περιλαμβάνει η κατασκευή ιστοσελίδας',
    heading: 'Μια επαγγελματική ιστοσελίδα δεν είναι μόνο εμφάνιση.',
    description:
      'Είναι δομή, περιεχόμενο και σωστή λειτουργία, ώστε ο επισκέπτης να καταλαβαίνει άμεσα τι κάνετε και πώς να επικοινωνήσει μαζί σας.',
  },
};

const pillarBase: Array<{ key: PillarKey; icon: LucideIcon }> = [
  { key: 'strategy', icon: Search },
  { key: 'experience', icon: Palette },
  { key: 'engineering', icon: ShieldCheck },
  { key: 'enablement', icon: NotebookPen },
] as const;

const pillarCopy: Record<Locale, Record<PillarKey, PillarCopy>> = {
  en: {
    strategy: {
      title: 'Structure & content',
      description:
        'We organize pages and messaging so visitors can find what they need quickly.',
      bullets: [
        'Recommendations for the key pages you need',
        'Clear navigation flow',
        'Content tailored to your audience',
      ],
    },
    experience: {
      title: 'Design & user experience',
      description:
        'We design websites that feel professional and build trust on every device.',
      bullets: [
        'Modern, clean design',
        'Great experience on mobile and tablet',
        'Easy navigation for all users',
      ],
    },
    engineering: {
      title: 'Development & performance',
      description:
        'Your website runs fast and reliably without technical issues.',
      bullets: [
        'Fast loading on all devices',
        'Stable operation without errors',
        'Ready to use from day one',
      ],
    },
    enablement: {
      title: 'Management & support',
      description:
        'We deliver a website you can manage yourself without technical knowledge.',
      bullets: [
        'Easy content management system',
        'Basic usage guidance',
        'Support after delivery when you need it',
      ],
    },
  },
  el: {
    strategy: {
      title: 'Δομή & περιεχόμενο',
      description:
        'Οργανώνουμε σωστά τις σελίδες και το μήνυμα της ιστοσελίδας, ώστε ο επισκέπτης να βρίσκει εύκολα αυτό που ψάχνει.',
      bullets: [
        'Προτάσεις για τις βασικές σελίδες που χρειάζεστε',
        'Ξεκάθαρη ροή πλοήγησης',
        'Περιεχόμενο προσαρμοσμένο στο κοινό σας',
      ],
    },
    experience: {
      title: 'Σχεδιασμός & εμπειρία χρήστη',
      description:
        'Σχεδιάζουμε ιστοσελίδες που δείχνουν επαγγελματισμό και εμπνέουν εμπιστοσύνη σε κάθε συσκευή.',
      bullets: [
        'Σύγχρονος και καθαρός σχεδιασμός',
        'Άριστη εμπειρία σε κινητό και tablet',
        'Εύκολη πλοήγηση για όλους τους χρήστες',
      ],
    },
    engineering: {
      title: 'Ανάπτυξη & απόδοση',
      description:
        'Η ιστοσελίδα σας λειτουργεί γρήγορα και σωστά, χωρίς τεχνικά προβλήματα.',
      bullets: [
        'Γρήγορη φόρτωση σε όλες τις συσκευές',
        'Σταθερή λειτουργία χωρίς σφάλματα',
        'Έτοιμη για χρήση από την πρώτη μέρα',
      ],
    },
    enablement: {
      title: 'Διαχείριση & υποστήριξη',
      description:
        'Παραδίδουμε μια ιστοσελίδα που μπορείτε να διαχειρίζεστε μόνοι σας, χωρίς τεχνικές γνώσεις.',
      bullets: [
        'Εύκολο σύστημα διαχείρισης περιεχομένου',
        'Βασικές οδηγίες χρήσης',
        'Υποστήριξη μετά την παράδοση όταν τη χρειαστείτε',
      ],
    },
  },
};

const processCopy: Record<Locale, ProcessStep[]> = {
  en: [
    {
      title: 'Start with clarity',
      description:
        'We discuss what you really need so the website serves your business.',
      bullets: [
        'What you do and who you serve',
        'The goal of the website',
        'Which pages are needed',
      ],
    },
    {
      title: 'Design before we build',
      description:
        'We present the structure and look of the website before development starts.',
      bullets: [
        'Page structure and navigation',
        'Design aligned with your brand',
        'Feedback and small revisions',
      ],
    },
    {
      title: 'Build & test',
      description:
        'We build the website and verify everything works correctly.',
      bullets: [
        'Looks right on mobile and desktop',
        'Fast and stable performance',
        'Final checks before going live',
      ],
    },
    {
      title: 'Launch & support',
      description:
        'Your website goes live and you can manage it with ease.',
      bullets: [
        'Delivered ready to use',
        'Basic management guidance',
        'Post-launch support when you need it',
      ],
    },
  ],
  el: [
    {
      title: 'Ξεκινάμε σωστά',
      description:
        'Συζητάμε τι χρειάζεστε πραγματικά, ώστε η ιστοσελίδα να εξυπηρετεί την επιχείρησή σας.',
      bullets: [
        'Τι κάνετε και σε ποιον απευθύνεστε',
        'Τι στόχο έχει η ιστοσελίδα',
        'Ποιες σελίδες χρειάζονται',
      ],
    },
    {
      title: 'Σχεδιάζουμε πριν χτίσουμε',
      description:
        'Σας παρουσιάζουμε τη δομή και την εικόνα της ιστοσελίδας πριν ξεκινήσει η κατασκευή.',
      bullets: [
        'Δομή σελίδων και πλοήγηση',
        'Σχεδιασμός προσαρμοσμένος στο brand σας',
        'Feedback και μικρές διορθώσεις',
      ],
    },
    {
      title: 'Υλοποιούμε & ελέγχουμε',
      description:
        'Κατασκευάζουμε την ιστοσελίδα και ελέγχουμε ότι όλα λειτουργούν σωστά.',
      bullets: [
        'Σωστή εμφάνιση σε κινητό και υπολογιστή',
        'Γρήγορη και σταθερή λειτουργία',
        'Τελικοί έλεγχοι πριν το ανέβασμα',
      ],
    },
    {
      title: 'Παραδίδουμε & υποστηρίζουμε',
      description:
        'Η ιστοσελίδα ανεβαίνει online και μπορείτε να τη διαχειρίζεστε με άνεση.',
      bullets: [
        'Έτοιμη για χρήση',
        'Βασικές οδηγίες διαχείρισης',
        'Υποστήριξη μετά το λανσάρισμα',
      ],
    },
  ],
};

const processIntro: Record<
  Locale,
  {
    eyebrow: string;
    heading: string;
    description: string;
  }
> = {
  en: {
    eyebrow: 'How the project moves forward',
    heading: 'From the first conversation to a live site',
    description:
      'Clear steps, no technical jargon and no surprises. You always know what is happening and what comes next.',
  },
  el: {
    eyebrow: 'Πώς προχωρά το έργο',
    heading: 'Από την πρώτη συζήτηση μέχρι το live site',
    description:
      'Ξεκάθαρα βήματα, χωρίς τεχνικούς όρους και χωρίς εκπλήξεις. Ξέρετε πάντα τι γίνεται και τι ακολουθεί.',
  },
};

const launchCopy: Record<
  Locale,
  {
    eyebrow: string;
    heading: string;
    description: string;
    groups: LaunchGroup[];
  }
> = {
  en: {
    eyebrow: 'After delivery',
    heading: 'Everything you need to use and evolve the site',
    description:
      'We deliver the website ready for daily use, with clear structure, guidance, and support—no technical knowledge required.',
    groups: [
      {
        title: 'Content & management',
        bullets: [
          'Easy content management system',
          'Edit text, images, and pages without hassle',
          'Structure that stays solid over time',
        ],
      },
      {
        title: 'Technical foundation & security',
        bullets: [
          'Stable and secure operation',
          'Basic backups and protection',
          'Ready for hosting and daily use',
        ],
      },
      {
        title: 'Support & next steps',
        bullets: [
          'Basic usage guidance',
          'Post-delivery support',
          'Option for future improvements when you need them',
        ],
      },
    ],
  },
  el: {
    eyebrow: 'Μετά την παράδοση',
    heading: 'Ό,τι χρειάζεστε για να χρησιμοποιείτε και να εξελίσσετε το site',
    description:
      'Παραδίδουμε την ιστοσελίδα έτοιμη για καθημερινή χρήση, με ξεκάθαρη δομή, οδηγίες και υποστήριξη, χωρίς να χρειάζονται τεχνικές γνώσεις.',
    groups: [
      {
        title: 'Διαχείριση & περιεχόμενο',
        bullets: [
          'Εύκολο σύστημα διαχείρισης περιεχομένου',
          'Αλλαγές σε κείμενα, εικόνες και σελίδες χωρίς δυσκολία',
          'Δομή που παραμένει σωστή και στο μέλλον',
        ],
      },
      {
        title: 'Τεχνική βάση & ασφάλεια',
        bullets: [
          'Σταθερή και ασφαλής λειτουργία',
          'Βασικά αντίγραφα ασφαλείας και προστασία',
          'Έτοιμο για φιλοξενία και καθημερινή χρήση',
        ],
      },
      {
        title: 'Υποστήριξη & επόμενα βήματα',
        bullets: [
          'Βασικές οδηγίες χρήσης',
          'Υποστήριξη μετά την παράδοση',
          'Δυνατότητα μελλοντικών βελτιώσεων όταν τις χρειαστείτε',
        ],
      },
    ],
  },
};

const launchGroupIcons: Array<{ key: number; icon: LucideIcon }> = [
  { key: 0, icon: Palette },
  { key: 1, icon: Gauge },
  { key: 2, icon: Megaphone },
] as const;

const faqIntroCopy: Record<Locale, FaqIntroContent> = {
  en: {
    eyebrow: 'FAQ',
    heading: 'Answers to common questions.',
    description: 'Still curious? Reach out and we’ll walk you through the details for your use case.',
  },
  el: {
    eyebrow: 'FAQ',
    heading: 'Απαντήσεις στις πιο συχνές ερωτήσεις.',
    description: 'Έχετε άλλη απορία; Επικοινωνήστε μαζί μας και θα σας καθοδηγήσουμε στο κατάλληλο πλάνο.',
  },
};

const faqCopy: Record<
  Locale,
  {
    question: string;
    answer: string;
  }[]
> = {
  en: [
    {
      question: 'How long does a typical web design project take?',
      answer:
        'Usually 2 to 6 weeks, depending on the size and needs of the project. The timeline is clarified from the start.',
    },
    {
      question: 'Will our team be able to manage the site after handover?',
      answer:
        'Yes. The website is delivered with an easy management system so you can update text, images, and basic content without technical knowledge.',
    },
    {
      question: 'Is SEO included in the delivery?',
      answer:
        'Basic SEO setup is included (structure, titles, speed, mobile-friendly). More advanced SEO can be handled in a separate package if you need it.',
    },
    {
      question: 'Do you handle hosting and domain management?',
      answer:
        'We can guide you or handle it for you, depending on what works best.',
    },
    {
      question: 'Do you offer post-launch maintenance?',
      answer:
        'Yes. There are support and maintenance options after delivery for updates, changes, or technical help whenever you need it.',
    },
    {
      question: 'Can you integrate e-commerce or custom functionality?',
      answer:
        'Yes. We can integrate e-commerce, forms, bookings, or other custom functionality based on your business needs.',
    },
  ],
  el: [
    {
      question: 'Πόσο διαρκεί ένα έργο κατασκευής ιστοσελίδας;',
      answer:
        'Συνήθως από 2 έως 6 εβδομάδες, ανάλογα με το μέγεθος και τις ανάγκες του έργου. Το χρονοδιάγραμμα ξεκαθαρίζεται από την αρχή.',
    },
    {
      question: 'Θα μπορούμε να διαχειριζόμαστε το site μετά την παράδοση;',
      answer:
        'Ναι. Η ιστοσελίδα παραδίδεται με εύκολο σύστημα διαχείρισης, ώστε να μπορείτε να αλλάζετε κείμενα, εικόνες και βασικό περιεχόμενο χωρίς τεχνικές γνώσεις.',
    },
    {
      question: 'Περιλαμβάνεται SEO στην παράδοση;',
      answer:
        'Περιλαμβάνεται βασική SEO ρύθμιση (δομή, τίτλοι, ταχύτητα, mobile-friendly). Πιο προχωρημένες ενέργειες SEO γίνονται σε ξεχωριστό πακέτο, αν το χρειάζεστε.',
    },
    {
      question: 'Αναλαμβάνετε hosting και domain;',
      answer:
        'Μπορούμε είτε να σας καθοδηγήσουμε είτε να τα αναλάβουμε εμείς, ανάλογα με το τι σας εξυπηρετεί καλύτερα.',
    },
    {
      question: 'Προσφέρετε συντήρηση μετά το λανσάρισμα;',
      answer:
        'Ναι. Υπάρχουν επιλογές υποστήριξης και συντήρησης μετά την παράδοση, για ενημερώσεις, αλλαγές ή τεχνική βοήθεια όταν τη χρειαστείτε.',
    },
    {
      question: 'Μπορείτε να ενσωματώσετε e-shop ή custom λειτουργίες;',
      answer:
        'Ναι. Μπορούμε να ενσωματώσουμε e-shop, φόρμες, κρατήσεις ή άλλες ειδικές λειτουργίες, ανάλογα με τις ανάγκες της επιχείρησής σας.',
    },
  ],
};

const closingCopy: Record<Locale, ClosingContent> = {
  en: {
    eyebrow: 'Partnership',
    title: 'Need a new website?',
    description:
      'We are here to help. Tell us the tone you want: more professional or more friendly.',
    highlights: [],
    primaryCta: 'Book a free consultation',
    secondaryCta: 'View service packages',
  },
  el: {
    eyebrow: 'Συνεργασία',
    title: 'Θέλετε νέα ιστοσελίδα;',
    description:
      'Είμαστε εδώ να σας βοηθήσουμε. Πες μου ποιο ύφος θες: πιο επαγγελματικό ή πιο φιλικό.',
    highlights: [],
    primaryCta: 'Κλείστε δωρεάν συμβουλευτική',
    secondaryCta: 'Δείτε πακέτα υπηρεσιών',
  },
};

export default function WebDesign() {
  const location = useLocation();
  const { language } = useLanguage();
  const localizedPath = useLocalizedPath();
  const locale = (language ?? 'el') as Locale;
  const metaData = getServiceMeta(serviceSlug, locale);
  const canonicalPath = withLanguagePrefix(stripLanguagePrefix(location.pathname), locale);
  const canonicalUrl = `${BASE_URL}${canonicalPath}`;
  const hreflang = getHreflangLinks(location.pathname, locale);
  const serviceDisplayName = locale === 'en' ? 'Web Design' : serviceName;
  const hero = heroCopy[locale];
  const approach = approachCopy[locale];
  const pillars = pillarCopy[locale];
  const processIntroCopy = processIntro[locale];
  const processSteps = processCopy[locale];
  const launch = launchCopy[locale];
  const faqIntro = faqIntroCopy[locale];
  const faqItems = faqCopy[locale];
  const closing = closingCopy[locale];

  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  useEffect(() => {
    trackServiceView(serviceSlug, language);
    const cleanup = trackScroll({ threshold: 0.75, once: true });
    return cleanup;
  }, [language]);

  const handleCTA = (ctaText: string) => {
    trackCTA(ctaText, window.location.pathname);
    window.navigateToConsultation?.();
  };

  const toggleFaq = (index: number) => {
    setOpenFaqIndex((current) => (current === index ? null : index));
  };

  return (
    <>
      <Seo
        title={metaData.title}
        description={metaData.description}
        url={canonicalUrl}
        image={metaData.image}
        hreflang={hreflang}
      />

      <JsonLd json={generateOrganizationSchema()} />
      <JsonLd json={generateWebsiteSchema(locale)} />
      <JsonLd json={generateBreadcrumbSchema(serviceDisplayName, serviceSlug, locale)} />
      <JsonLd json={generateServiceSchema(serviceDisplayName, metaData.description, canonicalUrl, 'Web Development')} />
      <JsonLd json={generateFAQSchema(faqItems)} />

      <div className="relative min-h-screen bg-[#0b0a0e] text-white">
        <PixelBackground />
        <Header variant="simple" />

        <main id="main-content" className="relative z-10 overflow-hidden">
          <HeroSection className="relative overflow-hidden pt-32 pb-20 md:pt-36 lg:pt-40 lg:pb-24">
            <div className="absolute inset-0 geometric-pattern" aria-hidden="true" />
            <div
              className="absolute inset-0 bg-gradient-to-br from-pp-black/95 via-black/85 to-black"
              aria-hidden="true"
            />
            <Container className="relative lg:min-h-[calc(100vh-220px)] lg:flex lg:flex-col lg:justify-center">
              <div className="max-w-3xl space-y-8">
                <div className="space-y-5">
                  <p className="text-[11px] font-medium uppercase tracking-[0.4em] text-brand-highlight/70">
                    {hero.eyebrow}
                  </p>
                  <Display
                    variant="h1"
                    as="h1"
                    className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.12] tracking-tight"
                  >
                    {hero.leading}
                  </Display>
                </div>

                <Display
                  variant="subhead"
                  as="p"
                  className="max-w-2xl text-base sm:text-lg leading-relaxed text-white/70"
                >
                  {hero.description}
                </Display>

                <div className="flex flex-col gap-4 sm:flex-row">
                  <Button
                    variant="primary"
                    size="lg"
                    className="w-full justify-center sm:w-auto"
                    onClick={() => handleCTA(hero.primaryCta)}
                  >
                    {hero.primaryCta}
                  </Button>
                  <Button
                    as="a"
                    href={localizedPath('/projects')}
                    variant="secondary"
                    size="lg"
                    className="w-full justify-center sm:w-auto"
                  >
                    {hero.secondaryCta}
                  </Button>
                </div>
              </div>

              <div className="mt-10 grid gap-6 border-t border-white/10 pt-8 sm:grid-cols-3 sm:gap-0 sm:divide-x sm:divide-white/10">
                {hero.metrics.map((metric) => (
                  <div key={metric.label} className="space-y-1 py-2 sm:px-6">
                    <p className="text-base font-semibold tracking-tight text-white md:text-base">{metric.value}</p>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-white/55">{metric.label}</p>
                  </div>
                ))}
              </div>
            </Container>
          </HeroSection>

          <Section>
            <Container>
              <div className="max-w-3xl space-y-4">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                  {approach.eyebrow}
                </p>
                <h2 className="text-3xl font-semibold text-white md:text-5xl">{approach.heading}</h2>
                <p className="text-lg text-white/70">{approach.description}</p>
              </div>

              <div className="mt-12 grid gap-8 md:grid-cols-2">
                {pillarBase.map(({ key, icon: Icon }) => {
                  const copy = pillars[key];
                  return (
                    <div
                      key={key}
                      className="group relative overflow-hidden rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl p-8 shadow-glass transition hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10"
                    >
                      <div className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 text-brand-highlight/80 transition group-hover:border-brand-highlight/60 group-hover:text-brand-highlight">
                        <Icon className="h-6 w-6" />
                      </div>
                      <h3 className="mt-6 text-2xl font-semibold text-white">{copy.title}</h3>
                      <p className="mt-3 text-sm text-white/70">{copy.description}</p>
                      <ul className="mt-6 space-y-3 text-sm text-white/75">
                        {copy.bullets.map((bullet) => (
                          <li key={bullet} className="flex items-start gap-3">
                            <CheckCircle className="mt-0.5 h-4 w-4 text-brand-highlight/70" />
                            <span className="leading-relaxed">{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </Container>
          </Section>

          <Section id="process" className="relative isolate">
            <div className="absolute inset-0 geometric-pattern opacity-20" aria-hidden="true" />
            <Container className="relative">
              <div className="max-w-3xl space-y-4 text-center md:mx-auto">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                  {processIntroCopy.eyebrow}
                </p>
                <h2 className="text-3xl font-semibold text-white md:text-5xl">{processIntroCopy.heading}</h2>
                <p className="text-lg text-white/70">{processIntroCopy.description}</p>
              </div>

              <div className="mt-16 grid gap-8 lg:grid-cols-2">
                {processSteps.map((step, index) => (
                  <div
                    key={step.title}
                    className="group flex h-full flex-col gap-4 rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl p-8 shadow-glass transition hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10"
                  >
                    <div className="flex items-center gap-4">
                      <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 text-lg font-semibold text-white transition group-hover:border-brand-highlight/60 group-hover:text-brand-highlight">
                        {(index + 1).toString().padStart(2, '0')}
                      </span>
                      <div>
                        {step.duration ? (
                          <p className="text-xs uppercase tracking-[0.3em] text-white/50">{step.duration}</p>
                        ) : null}
                        <h3 className={step.duration ? 'mt-1 text-2xl font-semibold text-white' : 'text-2xl font-semibold text-white'}>
                          {step.title}
                        </h3>
                      </div>
                    </div>
                    <p className="text-sm text-white/70">{step.description}</p>
                    {step.bullets?.length ? (
                      <ul className="mt-4 space-y-2 text-sm text-white/70">
                        {step.bullets.map((item) => (
                          <li key={item} className="flex items-start gap-2">
                            <span className="mt-1 h-1.5 w-1.5 rounded-full bg-brand-highlight/70" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    ) : step.deliverable ? (
                      <div className="mt-auto flex items-center gap-2 text-sm text-white/65 transition group-hover:text-white/80">
                        <CheckCircle className="h-4 w-4 text-brand-highlight/70 transition group-hover:text-brand-highlight" />
                        <span>{step.deliverable}</span>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </Container>
          </Section>

          <Section className="pt-0 pb-24 lg:pb-28">
            <Container>
              <div className="rounded-[32px] border border-white/15 bg-black/70 p-12 shadow-glass">
                <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-center lg:gap-16">
                  <div className="space-y-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                      {launch.eyebrow}
                    </p>
                    <h2 className="text-3xl font-semibold text-white md:text-5xl">{launch.heading}</h2>
                    <p className="text-lg text-white/70">{launch.description}</p>
                  </div>

                  <div className="grid gap-6 lg:grid-cols-3">
                    {launch.groups.map((group, index) => {
                      const Icon = launchGroupIcons[index]?.icon ?? Palette;
                      return (
                        <div
                          key={group.title}
                          className="group rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl p-6 transition hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10"
                        >
                          <div className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-brand-highlight/80 transition group-hover:border-brand-highlight/60 group-hover:text-brand-highlight">
                            <Icon className="h-5 w-5" />
                          </div>
                          <h3 className="mt-4 text-lg font-semibold text-white">{group.title}</h3>
                          <ul className="mt-4 space-y-2 text-sm text-white/75">
                            {group.bullets.map((item) => (
                              <li key={item} className="flex items-start gap-2">
                                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-white/40" />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </Container>
          </Section>

          <Section>
            <Container className="max-w-5xl">
              <div className="text-center">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                  {faqIntro.eyebrow}
                </p>
                <h2 className="mt-4 text-3xl font-semibold text-white md:text-5xl">
                  {faqIntro.heading}
                </h2>
                <p className="mt-4 text-lg text-white/70">{faqIntro.description}</p>
              </div>

              <div className="mt-12 space-y-4">
                {faqItems.map((faq, index) => {
                  const open = openFaqIndex === index;
                  return (
                    <div
                      key={faq.question}
                      className="overflow-hidden rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl transition hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10"
                    >
                      <button
                        type="button"
                        onClick={() => toggleFaq(index)}
                        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left text-base font-semibold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40"
                      >
                        <span>{faq.question}</span>
                        {open ? (
                          <ChevronUp className="h-5 w-5 text-white/60" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-white/60" />
                        )}
                      </button>
                      {open && (
                        <div className="border-t border-white/10 px-6 py-5 text-sm text-white/70">
                          <p className="leading-relaxed">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Container>
          </Section>

          <Section className="pt-0">
            <Container>
              <div className="relative overflow-hidden rounded-[32px] border border-white/15 bg-black/70 p-12 shadow-glass">
                <div className="pointer-events-none absolute -right-24 -top-32 h-72 w-72 rounded-full bg-brand-highlight/30 blur-3xl opacity-60" />
                <div className="pointer-events-none absolute -left-24 bottom-0 h-64 w-64 rounded-full bg-white/10 blur-3xl opacity-60" />
                <div className="relative">
                  <div className="mb-8 flex flex-col gap-3 rounded-2xl border border-white/10 bg-black/60 px-8 py-6 text-center">
                    <p className="text-xs font-semibold uppercase tracking-[0.35em] text-brand-highlight/80">
                      {closing.eyebrow}
                    </p>
                    <Display
                      variant="h1"
                      as="h2"
                      className="mx-auto max-w-4xl text-balance text-xl font-semibold text-white md:text-3xl"
                    >
                      {closing.title}
                    </Display>
                  </div>

                  <p className="mx-auto max-w-3xl text-center text-lg text-white/70">{closing.description}</p>

                  {closing.highlights.length ? (
                    <ul className="mx-auto mt-8 grid gap-3 text-left text-sm text-white/70 sm:grid-cols-3 sm:gap-4">
                      {closing.highlights.map((highlight) => (
                        <li
                          key={highlight}
                          className="group flex items-start gap-3 rounded-2xl border border-white/5 bg-black/60 px-5 py-4 transition hover:border-white/15"
                        >
                          <span className="mt-1 h-2 w-2 rounded-full bg-brand-highlight/70 transition-colors duration-300 group-hover:bg-brand-highlight" />
                          <span className="leading-relaxed">{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  <div className="mt-12 flex flex-col gap-4 text-center sm:flex-row sm:justify-center">
                    <Button
                      variant="primary"
                      size="lg"
                      className="w-full justify-center sm:w-auto"
                      onClick={() => handleCTA(closing.primaryCta)}
                      trackCta={{ text: closing.primaryCta, section: 'service_web_design_closing', variant: 'primary' }}
                    >
                      {closing.primaryCta}
                    </Button>
                    <Button
                      as="a"
                      href={localizedPath('/package-selection')}
                      variant="secondary"
                      size="lg"
                      className="w-full justify-center sm:w-auto"
                      onClick={(event) => {
                        event.preventDefault();
                        window.navigateToPackageSelection?.();
                      }}
                      trackCta={{ text: closing.secondaryCta, section: 'service_web_design_closing', variant: 'secondary', id: 'package_selection' }}
                    >
                      {closing.secondaryCta}
                    </Button>
                  </div>
                </div>
              </div>
            </Container>
          </Section>
        </main>

        <Footer variant="compact" />
      </div>
    </>
  );
}
